// User-facing error display helper. See src/admin/src/lib/errors.ts
// for the design rationale — kept symmetric between the two apps.
//
// Backend errors are bucketed:
//
// 1. **Expected / user-actionable** (validation, auth, rate-limit, ...) —
//    render the backend message verbatim.
// 2. **Everything else** (5xx, panics, transient infra) — render
//    "Something went wrong" + console.error the full detail. The
//    backend logs the panic stack to Axiom independently, so the
//    operator can correlate via request_id from the console.

import { APIError } from './api';

const SAFE_CODES = new Set<string>([
  // Validation
  'BAD_REQUEST',
  'INVALID_EMAIL',
  'INVALID_PRICE',
  'INVALID_TIER',
  'INVALID_PLAN',
  'INVALID_COMBO',
  'MISSING_FIELDS',
  'MISSING_TOKEN',
  'WEAK_PASSWORD',
  'TERMS_REQUIRED',
  'INVALID_BODY',
  'INVALID_JSON',
  // Auth / authz
  'UNAUTHORIZED',
  'INVALID_CREDENTIALS',
  'INVALID_TOKEN',
  'INVALID_TOTP',
  'SESSION_REVOKED',
  'PREMIUM_REQUIRED',
  'NOT_TEST_ACCOUNT',
  'OAUTH_VERIFY_FAILED',
  // Resource state
  'USER_NOT_FOUND',
  'EMAIL_TAKEN',
  'EMAIL_LINKED_ELSEWHERE',
  'ALREADY_DELETED',
  'ACCOUNT_GONE',
  'TOKEN_NOT_FOUND',
  'TOKEN_EXPIRED',
  'PROFILE_NOT_FOUND',
  'PROFILE_INCOMPLETE',
  'NOT_FOUND',
  'CONFLICT',
  'ALREADY_RATED',
  'COMMENT_TOO_LONG',
  // Feature gates
  'SIGNUPS_DISABLED',
  'STRIPE_NOT_CONFIGURED',
  'NO_SUBSCRIPTION',
  'CAPTCHA_REQUIRED',
  'CAPTCHA_FAILED',
  // Throttling
  'RATE_LIMITED',
]);

const GENERIC = 'Something went wrong. Please try again.';

export function friendlyError(err: unknown, context?: string): string {
  if (err instanceof APIError) {
    if (SAFE_CODES.has(err.code)) return err.message;
    console.error('[backend error]', context ?? '', err.code, err.message, err);
    return GENERIC;
  }
  if (err instanceof Error) {
    console.error('[unexpected error]', context ?? '', err);
    return GENERIC;
  }
  console.error('[unknown error]', context ?? '', err);
  return GENERIC;
}
